import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { localePath } from '../../i18n/locale-path';
import { countryConfig } from '../../country';
import { Seo } from '../../components/seo';
import { TenderList } from '../../components/tender-list';
import { QueryError, TenderListSkeleton } from '../../components/query-error';
import { usePageTitle } from '../../lib/page-title';
import { tenderPath } from '../../lib/slug';
import { Pagination } from '../../components/pagination';
import { listingPageHref, resolveListingPage } from '../../lib/pagination-paths';
import { UpgradeBanner } from '../../components/upgrade-banner';
import { fetchCatalog, fetchTopBuyersByCategory } from '../../lib/catalog-client';
import { tendersWord } from '../../lib/format';
import { useLocale, useT } from '../../i18n/locale-context';

const PAGE_SIZE = 20;

export function Category() {
  const { slug, page: pageParam } = useParams<{ slug: string; page?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = resolveListingPage(pageParam, searchParams.get('page'));
  const offset = (page - 1) * PAGE_SIZE;
  /** Root of this hub's crawlable pager (`/category/<slug>/page/N`). */
  const basePath = slug ? `/category/${slug}` : null;
  const { locale } = useLocale();
  const t = useT();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalog', 'category', slug, page, locale],
    queryFn: () => fetchCatalog({ category: slug, offset, limit: PAGE_SIZE, lang: locale }),
    enabled: !!slug,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // Contextual "top buyers in this category" — page-1 only (it's a hub feature),
  // seeded by the prerender so the /buyer/ links ship in crawlable HTML. Keyed
  // without locale: the org names are locale-agnostic, so both locale renders
  // seed one entry (matches the prerender bundle).
  const { data: topBuyers } = useQuery({
    queryKey: ['category-top-buyers', slug],
    queryFn: () => fetchTopBuyersByCategory(slug!),
    enabled: !!slug && page === 1,
    staleTime: 60 * 60 * 1000,
  });

  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next);
  }

  // Prefer the human-readable category name from the first result; fall back to slug.
  const categoryName = data?.items[0]?.category || slug || 'Category';
  usePageTitle(t('category.title', { category: categoryName }));

  return (
    <>
      <Seo
        // Page 2+ MUST get its own title/description. Until 2026-08-12 every page of a
        // series shipped the hub's, so /category/x/page/4 and /category/x/page/18 were
        // byte-identical in title, description and H1 across 306 URLs per locale — the
        // duplicate-metadata signal Google reads as "no reason to index this". The root
        // and results pagers already did this (`seo.catalog.titlePaged`); category and
        // region were simply missed.
        title={
          page > 1
            ? t('category.seoTitlePaged', { category: categoryName, page })
            : t('category.seoTitle', { category: categoryName })
        }
        description={
          page > 1
            ? t('category.seoDescPaged', {
                category: categoryName,
                page,
                totalPages,
                count: data?.total ?? 0,
              })
            : t('category.seoDesc', { category: categoryName, count: data?.total ?? 0 })
        }
        // SELF-canonical per page. Canonicalizing /page/N back to page 1 would
        // ask Google to drop the paginated hubs — and they are the only thing
        // giving this hub's tenders past the first 20 an internal link.
        canonical={basePath ? listingPageHref(basePath, page) : undefined}
        prev={basePath && page > 1 ? listingPageHref(basePath, page - 1) : undefined}
        next={basePath && page < totalPages ? listingPageHref(basePath, page + 1) : undefined}
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              // Was an empty shell (name only). Now lists the tenders rendered
              // on this hub, in display order, at their canonical URLs — the
              // same pattern the home catalog uses.
              '@type': 'ItemList',
              name: t('category.title', { category: categoryName }),
              itemListElement: (data?.items ?? []).map((item, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: `${countryConfig.brand.siteUrl}${localePath(tenderPath(item.slug, item.id), locale)}`,
                name: item.title,
              })),
            },
            {
              // Home → Category breadcrumb (Google renders it in the SERP).
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: t('nav.catalog'),
                  item: `${countryConfig.brand.siteUrl}${localePath('/', locale)}`,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: categoryName,
                  item: `${countryConfig.brand.siteUrl}${localePath(`/category/${slug ?? ''}`, locale)}`,
                },
              ],
            },
          ],
        }}
      />

      <Link
        to="/"
        className="mb-4 inline-block text-[13px] font-medium text-ink-soft transition-colors hover:text-brand"
      >
        {t('category.backToCatalog')}
      </Link>

      <h1 className="sr-only">{t('category.title', { category: categoryName })}</h1>
      {/* Unique, indexable intro so the hub isn't a zero-prose list of cards
          (thin-content flag). Rendered only when data is present so the count is
          real and it lands in the prerendered HTML. */}
      {!isLoading && data && (
        <p className="mb-4 max-w-3xl text-[13px] leading-[1.7] text-ink-soft">
          {t('category.intro', { category: categoryName, count: data.total })}
        </p>
      )}
      <p className="mb-4 text-[13px] text-ink-muted">
        {isLoading ? t('category.loading') : t('category.found', { count: data?.total ?? 0 })}
      </p>

      {isLoading ? (
        <TenderListSkeleton />
      ) : isError ? (
        <QueryError onRetry={() => void refetch()} />
      ) : (
        <>
          <TenderList items={data?.items ?? []} />
          {data && (
            <Pagination
              page={page}
              limit={PAGE_SIZE}
              total={data.total}
              onPageChange={goToPage}
              hrefFor={basePath ? (n) => listingPageHref(basePath, n) : undefined}
            />
          )}
        </>
      )}

      {/* Top заказчики in this category — contextual internal links from the hub
          to the buyer profiles, page-1 only. Prerender-seeded so the links are in
          the crawlable HTML, not client-only. */}
      {page === 1 && topBuyers && topBuyers.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-[15px] font-bold text-ink">{t('category.topBuyers')}</h2>
          <ul className="flex flex-col divide-y divide-edge-light overflow-hidden rounded-xl border border-edge bg-bone-surface shadow-m-sm">
            {topBuyers.map((b) => (
              <li key={b.tin}>
                <Link
                  to={`/buyer/${b.slug}`}
                  className="group flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-bone-hover"
                >
                  <span className="min-w-0 truncate font-semibold text-ink transition-colors group-hover:text-brand">
                    {b.name}
                  </span>
                  <span className="shrink-0 text-[12px] tabular-nums text-ink-muted">
                    {b.tenderCount} {tendersWord(b.tenderCount, locale)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* The banner carries no margin of its own — see its note. */}
      <div className="mt-5">
        <UpgradeBanner />
      </div>
    </>
  );
}
